'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import {
  Headphones,
  ShieldCheck,
  Award,
  Phone,
  ChevronDown,
  User,
  Shield,
  Menu,
  X,
  Gauge,
  Moon,
  Sun,
} from 'lucide-react';
import { ActivePageView } from '@/types';
import { useDashboardTheme } from '@/components/dashboard/useDashboardTheme';
import { SITE_LANGS, useLocale } from '@/lib/i18n';
import { usePublicBrand } from '@/components/site/PublicBrandProvider';
import type { DesignId } from '@/lib/storefront/designs';

/**
 * Per-design surface treatment. The navigation logic is shared: only the
 * surfaces, the eyebrow under the wordmark and the call-to-action shape change,
 * so the three designs read as different brands.
 */
const CHROME: Record<DesignId, { header: string; utilityBar: string | null; eyebrow: string; cta: string; drawer: string }> = {
  classic: {
    header: 'bg-white shadow-xs',
    utilityBar: 'bg-[var(--color-secondary)] text-[#9ca3af] border-b border-[#1f2937]/50',
    eyebrow: 'CAR RENTAL',
    cta: 'rounded-lg',
    drawer: 'bg-white border-b border-slate-200',
  },
  editorial: {
    header: 'bg-[#FAF6F0]',
    utilityBar: null,
    eyebrow: 'CAR RENTAL',
    cta: 'rounded-full',
    drawer: 'bg-[#FAF6F0] border-b border-[#e7ddd0]',
  },
  roadtrip: {
    header: 'bg-white shadow-sm',
    utilityBar: 'bg-[var(--color-secondary)] text-slate-300 border-b border-white/10',
    eyebrow: 'TRAVEL & RENTAL',
    cta: 'rounded-full',
    drawer: 'bg-white border-b border-slate-200',
  },
};

function BrandWordmark({ name }: { name: string }) {
  return <>{name.trim() || 'fleetava'}</>;
}

const THEME_LABEL: Record<'light' | 'dark', Record<string, string>> = {
  light: { fr: 'Mode clair', en: 'Light mode', ar: 'الوضع الفاتح', es: 'Modo claro' },
  dark: { fr: 'Mode sombre', en: 'Dark mode', ar: 'الوضع الداكن', es: 'Modo oscuro' },
};

function ThemeToggle({ tone }: { tone: 'dark' | 'light' }) {
  const { lang } = useLocale();
  const { theme, toggleTheme } = useDashboardTheme();
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const idle = tone === 'dark' ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={THEME_LABEL[nextTheme][lang] ?? THEME_LABEL[nextTheme].en}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${idle}`}
    >
      {theme === 'dark' ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
    </button>
  );
}

function LanguageMenu({ tone }: { tone: 'dark' | 'light' }) {
  const { lang, setLang } = useLocale();
  const [open, setOpen] = useState(false);
  const current = SITE_LANGS.find((entry) => entry.code === lang) ?? SITE_LANGS[0];
  const label = lang === 'ar' ? 'اللغة' : lang === 'en' ? 'Language' : 'Langue';
  const trigger = tone === 'dark'
    ? 'text-slate-300 hover:text-white'
    : 'text-xs font-semibold text-slate-600 hover:text-slate-900';
  const menu = tone === 'dark'
    ? 'bg-slate-900 border-slate-700'
    : 'bg-white border-slate-200';
  const item = tone === 'dark' ? 'text-slate-300' : 'text-slate-700';

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        className={`flex items-center gap-1 cursor-pointer transition-colors ${trigger}`}
      >
        <span>{current.short}</span>
        <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {open && (
        <div className={`absolute right-0 z-50 mt-1 w-28 rounded border py-1 text-xs shadow-xl ${menu}`}>
          {SITE_LANGS.map((entry) => (
            <button
              key={entry.code}
              type="button"
              onClick={() => {
                setLang(entry.code);
                setOpen(false);
              }}
              className={`w-full px-3 py-1 text-left hover:bg-amber-500 hover:text-white ${
                entry.code === lang ? 'bg-amber-500 text-white' : item
              }`}
            >
              {entry.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface NavbarProps {
  currentView: ActivePageView;
  onNavigate: (view: ActivePageView) => void;
  onOpenBookingModal: () => void;
  activeBookingsCount: number;
  currency: string;
  onCurrencyChange: (code: string) => void;
  /** Hidden when the appearance editor disables the deals section. */
  showDeals?: boolean;
  /** Chosen in the appearance editor; drives the chrome surface. */
  design?: DesignId;
}

export function Navbar({
  currentView,
  onNavigate,
  onOpenBookingModal,
  activeBookingsCount,
  currency,
  onCurrencyChange,
  showDeals = true,
  design = 'classic',
}: NavbarProps) {
  const chrome = CHROME[design] ?? CHROME.classic;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLocale();
  const brand = usePublicBrand();
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);

  const navLinks: { label: string; view: ActivePageView }[] = [
    { label: t('nav.home'), view: 'home' },
    { label: t('nav.vehicles'), view: 'vehicles' },
    { label: t('nav.locations'), view: 'locations' },
    ...(showDeals ? [{ label: t('nav.deals'), view: 'deals' as ActivePageView }] : []),
    { label: t('nav.services'), view: 'services' },
    { label: t('nav.about'), view: 'about' },
    { label: t('nav.contact'), view: 'contact' },
  ];

  return (
    <header className={`w-full sticky top-0 z-50 ${chrome.header}`}>
      {/* Main navigation sits above the utility strip. */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-8 py-3.5 flex items-center justify-between gap-3 sm:gap-6">
        {/* Brand Logo - Exact Speedometer + fleetava CAR RENTAL */}
        <button
          type="button"
          onClick={() => onNavigate('home')}
          className="flex items-center gap-2.5 text-left cursor-pointer group min-w-0"
        >
          <div className="relative w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform shrink-0 overflow-hidden">
            {brand.logoUrl ? (
              <Image src={brand.logoUrl} alt="" width={40} height={40} unoptimized className="h-full w-full object-cover" />
            ) : (
              <Gauge className="w-6 h-6 text-[var(--color-primary)]" />
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <div className="text-xl sm:text-2xl font-black tracking-tight leading-none text-slate-900 font-sans truncate">
              <BrandWordmark name={brand.shortName || brand.name} />
            </div>
            <span className="text-[9px] tracking-[0.25em] font-bold text-slate-500 uppercase mt-0.5">
              {chrome.eyebrow}
            </span>
          </div>
        </button>

        {/* Desktop Navigation Links */}
        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => {
            const isActive = currentView === link.view;
            return (
              <button
                key={link.view}
                type="button"
                onClick={() => onNavigate(link.view)}
                className={`text-xs font-bold tracking-wider transition-colors relative py-1 cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'text-[var(--color-primary)]'
                    : 'text-slate-700 hover:text-[var(--color-primary)]'
                }`}
              >
                {link.label}
                {isActive && (
                  <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--color-primary)] rounded-full" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Right CTA Button and Navigation Actions */}
        <div className="flex items-center gap-3">
          {!chrome.utilityBar && (
            <>
              <ThemeToggle tone="light" />
              <LanguageMenu tone="light" />
            </>
          )}
          {/* User Profile shortcut for tablet/desktop */}
          <button
            type="button"
            onClick={() => onNavigate('profile')}
            className={`hidden sm:flex lg:hidden items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer ${
              currentView === 'profile'
                ? 'bg-amber-100 text-amber-800'
                : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <User className="w-3.5 h-3.5" />
            <span>{t('nav.profile')}</span>
          </button>

          {/* BOOK NOW CTA (Identical to image: Bold Vibrant Orange) */}
          <button
            type="button"
            onClick={onOpenBookingModal}
            className={`px-4 sm:px-6 py-2.5 text-xs font-bold tracking-wider uppercase text-white bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 ${chrome.cta} shadow-sm hover:shadow-md transition-all cursor-pointer whitespace-nowrap`}
          >
            {t('nav.bookNow')}
          </button>

          {/* Mobile Menu Toggle Button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-slate-700 hover:text-slate-900 rounded-lg hover:bg-slate-100 cursor-pointer"
            aria-label="Toggle Navigation Menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {chrome.utilityBar && (
      <div className={`w-full ${chrome.utilityBar} text-xs py-2 px-4 sm:px-8`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-7 overflow-x-auto no-scrollbar min-w-0">
            <div className="flex items-center gap-1.5 whitespace-nowrap text-slate-300">
              <Headphones className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span className="truncate">{t('nav.support')}</span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 whitespace-nowrap text-slate-300">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span>{t('nav.freeCancel')}</span>
            </div>
            <div className="hidden md:flex items-center gap-1.5 whitespace-nowrap text-slate-300">
              <Award className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span>{t('nav.bestPrice')}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-6 shrink-0">
            <a
              href={brand.phone ? `tel:${brand.phone.replace(/\s/g, '')}` : undefined}
              className="flex items-center gap-1.5 text-slate-200 hover:text-[var(--color-primary)] transition-colors whitespace-nowrap"
            >
              <Phone className="w-3.5 h-3.5 shrink-0 text-[var(--color-primary)]" />
              <span className="font-medium hidden min-[480px]:inline truncate">{brand.phone || brand.city}</span>
            </a>

            <LanguageMenu tone="dark" />

            <div className="relative">
              <button
                type="button"
                onClick={() => setCurrencyDropdownOpen(!currencyDropdownOpen)}
                className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors cursor-pointer"
              >
                <span>{currency}</span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>
              {currencyDropdownOpen && (
                <div className="absolute right-0 mt-1 py-1 w-20 bg-slate-900 border border-slate-700 rounded shadow-xl z-50 text-xs">
                  {['MAD', 'EUR', 'USD'].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        onCurrencyChange(c);
                        setCurrencyDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-1 text-slate-300 hover:bg-amber-500 hover:text-white"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <ThemeToggle tone="dark" />

            <div className="hidden lg:flex items-center gap-2 pl-2 border-l border-slate-700">
              <button
                type="button"
                onClick={() => onNavigate('profile')}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors cursor-pointer ${
                  currentView === 'profile'
                    ? 'bg-amber-500/20 text-amber-400 font-semibold'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <User className="w-3 h-3 text-amber-400" />
                <span>{t('nav.myProfile')}</span>
                {activeBookingsCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.2 bg-amber-500 text-slate-950 font-bold text-[10px] rounded-full">
                    {activeBookingsCount}
                  </span>
                )}
              </button>

              <a
                href="/admin"
                className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-slate-300 transition-colors hover:text-white"
              >
                <Shield className="w-3 h-3 text-amber-400" />
                <span>Admin Fleet</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Mobile Drawer Navigation */}
      {mobileMenuOpen && (
        <div className={`lg:hidden ${chrome.drawer} px-4 sm:px-6 py-4 shadow-lg animate-in slide-in-from-top duration-200 max-h-[70vh] overflow-y-auto`}>
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => {
              const isActive = currentView === link.view;
              return (
                <button
                  key={link.view}
                  type="button"
                  onClick={() => {
                    onNavigate(link.view);
                    setMobileMenuOpen(false);
                  }}
                  className={`text-left py-2 text-sm font-bold tracking-wider cursor-pointer ${
                    isActive ? 'text-[var(--color-primary)]' : 'text-slate-700 hover:text-[var(--color-primary)]'
                  }`}
                >
                  {link.label}
                </button>
              );
            })}
            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
              {!chrome.utilityBar && <ThemeToggle tone="light" />}
              <button
                type="button"
                onClick={() => {
                  onOpenBookingModal();
                  setMobileMenuOpen(false);
                }}
                className={`w-full py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white text-xs font-bold uppercase tracking-wider ${chrome.cta} shadow-sm transition-all cursor-pointer`}
              >
                {t('nav.bookNowTitle')}
              </button>
              <button
                type="button"
                onClick={() => {
                  onNavigate('profile');
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 py-2 text-sm font-semibold text-slate-700 hover:text-amber-600"
              >
                <User className="w-4 h-4 text-amber-500" />
                <span>{t('nav.drawerProfile')} ({activeBookingsCount})</span>
              </button>
              <a
                href="/admin"
                className="flex items-center gap-2 py-2 text-sm font-semibold text-slate-700 hover:text-amber-600"
              >
                <Shield className="w-4 h-4 text-amber-500" />
                <span>{t('nav.drawerAdmin')}</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
